// ============================================================
// The combat-state write clock.
//
// `CombatState.rev` is a Lamport clock advanced by every LOCAL MUTATION
// - never by the act of writing to Firestore. Getting that backwards is
// what made v7.5.0 fail: bumping on send meant two clients working from
// the same base both emitted `R+1`, so the stale write did not look
// older and was still accepted.
//
// Bumping on mutation gives the property the fix actually needs: a
// client that changed nothing keeps its old rev, so when its earlier
// write echoes back late it is provably behind and gets refused.
// ============================================================
import { canonicalJson } from '../canonicalJson';
import type { Combatant, CombatState } from './types';

/** A state's content, with the clock removed - for comparison. */
export function stateContent(state: CombatState): Omit<CombatState, 'rev'> {
  const { rev: _rev, ...content } = state;
  return content;
}

/**
 * Stamp `next` with the right clock relative to `prev`.
 *
 * Unchanged content keeps the clock still, which matters: a no-op
 * mutation must not make this client look newer than everyone else.
 */
export function bumpRev(prev: CombatState, next: CombatState): CombatState {
  // By value, never by key order: a rebuild that only reorders keys is no change (v8.393.3).
  if (canonicalJson(stateContent(prev)) === canonicalJson(stateContent(next))) {
    return { ...next, rev: prev.rev ?? 0 };
  }
  return { ...next, rev: (prev.rev ?? 0) + 1 };
}

/** Cold-half change detection by IDENTITY - the mutators reuse
 * untouched references, so no JSON ever needs stringifying here. A
 * replaced-but-identical object bumps unnecessarily, which is harmless
 * clock inflation (the veto is >=, the write gate compares content). */
function coldChanged(prev: Combatant, next: Combatant): boolean {
  return COLD_FIELDS.some((field) => prev[field] !== next[field]);
}

/** The cold half of a combatant: everything but the live state. */
const COLD_FIELDS = ['sheet', 'overrides', 'name', 'kind', 'order', 'loadoutRef', 'slotOrder'] as const;

/**
 * Stamp `sheetRev` across a roster transition - the ONE place the cold
 * clock advances, wrapped around the roster setter so no mutation site
 * can forget it (the state clock bumps per-mutator; the cold half has
 * a dozen mutators and central stamping is what makes them all safe).
 *
 * `max(explicit, bump)`: the sync listener sets the remote clock on
 * adoption, and a local mutation must never move the clock backwards
 * past it.
 */
export function stampSheetRevs(prev: Combatant[], next: Combatant[]): Combatant[] {
  if (prev === next) return next;
  const byId = new Map(prev.map((c) => [c.id, c]));
  const out = next.map((n) => {
    const p = byId.get(n.id);
    return !p || p === n ? n : withSheetRev(p, n);
  });
  return out.some((c, i) => c !== next[i]) ? out : next;
}

/** `n` with its sheet clock at least `p`'s, one past it when the cold half changed; `n` itself when already right. */
function withSheetRev(p: Combatant, n: Combatant): Combatant {
  const floor = p.sheetRev ?? 0;
  const current = n.sheetRev ?? 0;
  const stamped = Math.max(current, coldChanged(p, n) ? floor + 1 : floor);
  if (stamped === current && n.sheetRev !== undefined) return n;
  return { ...n, sheetRev: stamped };
}
